<template>
  <div class="border border-slate-200 bg-white p-4 shadow-sm">
    <h3 class="mb-3 text-base font-semibold text-slate-800">{{ title }}</h3>
    <div
      v-if="sensorType === 'door_open'"
      class="mb-2 flex flex-wrap items-center gap-4 text-xs text-slate-500">
      <span class="inline-flex items-center gap-1.5">
        <span class="inline-block h-3 w-4 bg-yellow-400" />
        {{ t('sensor.group_alarm_detail.state_closed') }}
      </span>
      <span class="inline-flex items-center gap-1.5">
        <span class="inline-block h-3 w-4 bg-red" />
        {{ t('sensor.group_alarm_detail.state_open') }}
      </span>
    </div>
    <div ref="chartContainer" class="h-80 w-full min-h-[20rem]" />
    <p v-if="isLoading && !hasData" class="mt-2 text-center text-sm text-slate-500">
      {{ t('sensor.group_alarm_detail.charts.loading') }}
    </p>
    <p v-else-if="!isLoading && !hasData" class="mt-2 text-center text-sm text-slate-500">
      {{ t('sensor.group_alarm_detail.charts.no_data') }}
    </p>
  </div>
</template>

<script setup lang="ts">
import SensorResource from '@/api/resources/sensor'
import { useI18n } from '@/composable/useI18n'
import type { ISensor } from '@/interfaces/entities/sensor'
import {
  buildAlarmMarkArea,
  buildAlarmMarkLine,
} from '@/utils/components/alarm-detail'
import {
  buildGroupAlarmChartOption,
  type GroupAlarmSeries,
} from '@/utils/components/alarm-detail/group-alarm-chart-options'
import {
  buildMultiRowDoorOpenEchartsStateTimelineOption,
  type DoorOpenMultiRowInput,
} from '@/utils/components/door-open-chart'
import { normalizeEnergyReadingsForChart } from '@/utils/energy-readings-normalize'
import {
  mapDoorOpenVoltageToOpenPeriods,
  parseTimeDoorOpen,
} from '@/utils/door-open-periods'
import type { EChartsOption } from 'echarts'
import * as echarts from 'echarts'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const { t } = useI18n()

const props = defineProps<{
  sensorType: 'electricity' | 'temperature' | 'door_open'
  sensors: ISensor[]
  startDate: string
  endDate: string | null
}>()

const chartContainer = ref<HTMLDivElement | null>(null)
let chartInstance: echarts.ECharts | null = null
const isLoading = ref(false)
const savedDataZoom = ref({ start: 0, end: 100 })

/** Séries lineares (energia/temperatura): uma linha por sensor. */
const lineSeries = ref<GroupAlarmSeries[]>([])
/** Linhas do state-timeline de portas: uma por sensor. */
const doorRows = ref<DoorOpenMultiRowInput[]>([])
const doorRange = ref<{ start: number; end: number } | null>(null)
const doorHasFetched = ref(false)

const title = computed(() => {
  if (props.sensorType === 'temperature') return t('sensor.group_alarm_detail.charts.temperature')
  if (props.sensorType === 'door_open') return t('sensor.group_alarm_detail.charts.door_open')
  return t('sensor.group_alarm_detail.charts.energy')
})

const alarmPeriod = computed<[number, number]>(() => {
  const start = new Date(props.startDate).getTime()
  const end = props.endDate ? new Date(props.endDate).getTime() : Date.now()
  return [start, end]
})

const hasData = computed(() => {
  if (props.sensorType === 'door_open') {
    return doorHasFetched.value && doorRange.value != null
  }
  return lineSeries.value.some((s) => s.data.length > 0)
})

/** Padding em ms (1 hora antes e depois) */
const PADDING_MS = 60 * 60 * 1000

function buildDateRange(): { dateRange: string; resolution: string; start: number; end: number } | null {
  if (!props.startDate) return null
  const start = new Date(props.startDate).getTime() - PADDING_MS
  const end = props.endDate ? new Date(props.endDate).getTime() + PADDING_MS : Date.now() + PADDING_MS
  const startISO = new Date(start).toISOString()
  const endISO = new Date(end).toISOString()
  const hours = (end - start) / (60 * 60 * 1000)
  const resolution = hours > 48 ? '15m' : '1m'
  const dateRange = `start_date=${encodeURIComponent(startISO)}&end_date=${encodeURIComponent(endISO)}`
  return { dateRange, resolution, start, end }
}

function parseTemperatureReadings(readings: unknown): [number, number][] {
  const raw = Array.isArray(readings)
    ? readings
    : readings && typeof readings === 'object' && Array.isArray((readings as any).temperature)
      ? (readings as any).temperature
      : readings && typeof readings === 'object' && Array.isArray((readings as any).Temperatura)
        ? (readings as any).Temperatura
        : []
  return raw
    .map((r: { t?: string; v?: number; time?: string; value?: number }) => {
      const timeStr = r.t ?? r.time
      const val =
        typeof (r.v ?? r.value) === 'number' && !Number.isNaN(Number(r.v ?? r.value))
          ? Number(r.v ?? r.value)
          : null
      if (!timeStr || val === null) return [0, 0] as [number, number]
      return [new Date(timeStr).getTime(), val] as [number, number]
    })
    .filter(([ts]) => ts > 0)
}

async function fetchElectricity(range: { dateRange: string; resolution: string }) {
  const results = await Promise.allSettled(
    props.sensors.map((sensor) =>
      SensorResource.getEnergySensorReadings({
        sensorId: sensor.id,
        dateRange: range.dateRange,
        resolution: range.resolution,
        aggregation: 'mean',
      }),
    ),
  )

  const series: GroupAlarmSeries[] = []
  results.forEach((result, sensorIndex) => {
    if (result.status !== 'fulfilled') return
    const raw = result.value?.readings
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return
    const norm = normalizeEnergyReadingsForChart(raw as Record<string, Array<{ t?: string; v?: number }>>)
    const phases: Array<{ suffix: string; data: [number, number][] }> = [
      //{ suffix: 'A', data: norm.current_a },
      //{ suffix: 'B', data: norm.current_b },
      //{ suffix: 'C', data: norm.current_c },
      { suffix: 'Média', data: norm.average },
    ]
    phases.forEach((phase, phaseIndex) => {
      if (phase.data.length === 0) return
      series.push({
        name: `${props.sensors[sensorIndex].name} · ${phase.suffix}`,
        data: phase.data,
        colorIndex: sensorIndex,
        dashIndex: phaseIndex,
      })
    })
  })
  lineSeries.value = series
}

async function fetchTemperature(range: { dateRange: string; resolution: string }) {
  const results = await Promise.allSettled(
    props.sensors.map((sensor) =>
      SensorResource.getTemperatureSensorReadings({
        sensorId: sensor.id,
        dateRange: range.dateRange,
        resolution: range.resolution,
      }),
    ),
  )

  const series: GroupAlarmSeries[] = []
  results.forEach((result, sensorIndex) => {
    if (result.status !== 'fulfilled') return
    const data = parseTemperatureReadings(result.value?.readings)
    if (data.length === 0) return
    series.push({
      name: props.sensors[sensorIndex].name,
      data,
      colorIndex: sensorIndex,
    })
  })
  lineSeries.value = series
}

async function fetchDoorOpen(range: { dateRange: string; resolution: string; start: number; end: number }) {
  const results = await Promise.allSettled(
    props.sensors.map((sensor) =>
      SensorResource.getOpenSensorReadings({
        sensorId: sensor.id,
        dateRange: range.dateRange,
        resolution: '1m',
      }),
    ),
  )

  const rows: DoorOpenMultiRowInput[] = []
  results.forEach((result, sensorIndex) => {
    if (result.status !== 'fulfilled') return
    const readings = result.value?.readings as Record<string, unknown> | undefined
    const fromTimeDoorOpen = parseTimeDoorOpen(readings)
    const voltageReadings = (readings?.door_open_voltage ?? []) as Array<{
      t?: string
      v?: number
      s?: string
    }>
    const periods =
      fromTimeDoorOpen.length > 0
        ? fromTimeDoorOpen
        : mapDoorOpenVoltageToOpenPeriods(voltageReadings, range.end)
    rows.push({
      label: props.sensors[sensorIndex].name,
      openPeriods: periods.map((period) => ({
        y: [
          new Date(period.start_date).getTime(),
          period.end_date ? new Date(period.end_date).getTime() : range.end,
        ] as [number, number],
      })),
    })
  })
  doorRows.value = rows
  doorRange.value = { start: range.start, end: range.end }
  doorHasFetched.value = true
}

async function fetchReadings() {
  if (props.sensors.length === 0 || !props.startDate) return
  const range = buildDateRange()
  if (!range) return
  isLoading.value = true
  try {
    if (props.sensorType === 'electricity') {
      await fetchElectricity(range)
    } else if (props.sensorType === 'temperature') {
      await fetchTemperature(range)
    } else {
      await fetchDoorOpen(range)
    }
    savedDataZoom.value = { start: 0, end: 100 }
  } catch {
    lineSeries.value = []
    doorRows.value = []
    doorRange.value = null
    doorHasFetched.value = true
    savedDataZoom.value = { start: 0, end: 100 }
  } finally {
    isLoading.value = false
  }
}

function saveDataZoom() {
  if (!chartInstance) return
  const opts = chartInstance.getOption() as { dataZoom?: Array<{ start?: number; end?: number }> }
  const dz = opts.dataZoom?.[0]
  if (dz && typeof dz.start === 'number' && typeof dz.end === 'number') {
    savedDataZoom.value = { start: dz.start, end: dz.end }
  }
}

function initChart() {
  if (!chartContainer.value) return
  if (chartInstance) {
    chartInstance.dispose()
    chartInstance = null
  }
  chartInstance = echarts.init(chartContainer.value)
  chartInstance.on('datazoom', saveDataZoom)
  updateChart()
}

function updateChart() {
  if (!chartInstance) return

  if (props.sensorType === 'door_open') {
    const range = doorRange.value
    if (!range || range.end <= range.start || doorRows.value.length === 0) {
      chartInstance.clear()
      return
    }

    const option = buildMultiRowDoorOpenEchartsStateTimelineOption({
      rows: doorRows.value,
      rangeStart: range.start,
      rangeEnd: range.end,
      alarmPeriods: [alarmPeriod.value],
      dataZoom: savedDataZoom.value,
    })

    chartInstance.setOption(option, true)
    return
  }

  if (lineSeries.value.length === 0) {
    chartInstance.clear()
    return
  }

  const option = buildGroupAlarmChartOption({
    series: lineSeries.value,
    unit: props.sensorType === 'temperature' ? '°C' : 'A',
    dataZoom: savedDataZoom.value,
    markArea: buildAlarmMarkArea(alarmPeriod.value),
    markLine: buildAlarmMarkLine(alarmPeriod.value),
    sliderBottom: 8,
    decimals: props.sensorType === 'temperature' ? 1 : 2,
  })

  chartInstance.setOption(option)
}

watch(
  () => [lineSeries.value, doorRows.value, doorRange.value, alarmPeriod.value],
  () => nextTick(() => updateChart()),
  { deep: true },
)

watch(
  () => [props.sensorType, props.startDate, props.endDate],
  () => {
    lineSeries.value = []
    doorRows.value = []
    doorRange.value = null
    doorHasFetched.value = false
    fetchReadings().then(() => nextTick(() => updateChart()))
  },
  { deep: true },
)

watch(
  () => props.sensors.map((s) => s.id).join(','),
  () => {
    lineSeries.value = []
    doorRows.value = []
    doorRange.value = null
    doorHasFetched.value = false
    fetchReadings().then(() => nextTick(() => updateChart()))
  },
)

const handleResize = () => chartInstance?.resize()

onMounted(() => {
  fetchReadings().then(() => nextTick(() => initChart()))
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  if (chartInstance) {
    chartInstance.dispose()
    chartInstance = null
  }
})
</script>
